import {
    HeadContent,
    Link,
    Scripts,
    useRouter,
} from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

import { Cross } from '@/components/site/shell';
import { Button } from '@/components/ui/button';
import {
    formatAppErrorReport,
    inspectAppError,
    type AppErrorDetails,
} from '@/lib/errors';
import { SITE_NAME } from '@/lib/site-url';
import { cn } from '@/lib/utils';

function kindBadgeClass(kind: AppErrorDetails['kind']) {
    switch (kind) {
        case 'database':
            return 'border-brand-accent/40 text-brand-accent';
        case 'query':
            return 'border-brand-warm/40 text-brand-warm';
        case 'network':
            return 'border-brand/40 text-brand';
        default:
            return 'border-white/20 text-white/55';
    }
}

function kindLabel(kind: AppErrorDetails['kind']) {
    switch (kind) {
        case 'database':
            return 'database';
        case 'query':
            return 'query';
        case 'network':
            return 'network';
        default:
            return 'error';
    }
}

function copyText(text: string): boolean {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.left = '0';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    el.setSelectionRange(0, text.length);
    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch {
        ok = false;
    }
    document.body.removeChild(el);
    return ok;
}

export function AppErrorPage({
    error,
    reset,
    info,
}: ErrorComponentProps) {
    const router = useRouter();
    const details = inspectAppError(error);
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

    const retry = () => {
        reset();
        void router.invalidate();
    };

    const copyReport = () => {
        const report = formatAppErrorReport(details, {
            url: typeof window !== 'undefined' ? window.location.href : undefined,
            componentStack: info?.componentStack ?? undefined,
        });
        const ok = copyText(report);
        if (ok) {
            setCopyState('copied');
            window.setTimeout(() => setCopyState('idle'), 2000);
            return;
        }
        if (!navigator.clipboard?.writeText) {
            setCopyState('failed');
            window.setTimeout(() => setCopyState('idle'), 2000);
            return;
        }
        void Promise.race([
            navigator.clipboard.writeText(report).then(
                () => true,
                () => false,
            ),
            new Promise<boolean>((resolve) => {
                window.setTimeout(() => resolve(false), 1000);
            }),
        ]).then((copied) => {
            setCopyState(copied ? 'copied' : 'failed');
            window.setTimeout(() => setCopyState('idle'), 2000);
        });
    };

    return (
        <div
            dir="rtl"
            lang="he"
            className="dark flex min-h-screen flex-col bg-black text-[#ededed] antialiased"
        >
            <header className="border-b border-white/10">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between border-x border-white/10 px-6">
                    <Link to="/" className="inline-flex shrink-0 items-center">
                        <img
                            src="/images/logo.svg"
                            alt={SITE_NAME}
                            className="h-8 w-auto"
                            width={120}
                            height={32}
                        />
                    </Link>
                    <p className="font-mono text-[11px] text-white/55" dir="ltr">
                        {kindLabel(details.kind)}
                    </p>
                </div>
            </header>

            <main className="relative flex flex-1 flex-col px-6 py-12">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                        backgroundImage:
                            'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
                        backgroundSize: '28px 28px',
                        maskImage:
                            'radial-gradient(ellipse 60% 55% at 50% 30%, black, transparent 75%)',
                        WebkitMaskImage:
                            'radial-gradient(ellipse 60% 55% at 50% 30%, black, transparent 75%)',
                    }}
                />

                <div className="relative mx-auto w-full max-w-3xl border border-white/10 bg-black">
                    <Cross className="-start-[5px] -top-2.5" />
                    <Cross className="-end-[5px] -top-2.5" />
                    <Cross className="-bottom-2.5 -start-[5px]" />
                    <Cross className="-bottom-2.5 -end-[5px]" />

                    <div className="flex items-center justify-between gap-4 border-b border-white/10 px-6 py-3">
                        <p className="text-[10px] text-white/55">שגיאה</p>
                        <span
                            className={cn(
                                'border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
                                kindBadgeClass(details.kind),
                            )}
                            dir="ltr"
                        >
                            {kindLabel(details.kind)}
                        </span>
                    </div>

                    <div className="space-y-6 p-6 sm:p-8">
                        <div>
                            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                                {details.title}
                            </h1>
                            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/50">
                                אירעה שגיאה בטעינת העמוד. הפרטים הטכניים מוצגים
                                למטה כדי לאפשר איתור מהיר של הבעיה.
                            </p>
                        </div>

                        <div
                            className="border border-white/10 bg-white/[0.03] p-4"
                            dir="ltr"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">
                                Summary
                            </p>
                            <p className="mt-2 whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-white/85">
                                {details.summary}
                            </p>
                        </div>

                        {details.fields.length > 0 ? (
                            <div className="border border-white/10" dir="ltr">
                                <div className="border-b border-white/10 px-4 py-2">
                                    <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">
                                        Details
                                    </p>
                                </div>
                                <dl className="divide-y divide-white/10">
                                    {details.fields.map((field) => (
                                        <div
                                            key={`${field.label}:${field.value.slice(0, 48)}`}
                                            className="grid gap-1 px-4 py-3 sm:grid-cols-[9rem_1fr] sm:gap-4"
                                        >
                                            <dt className="font-mono text-[11px] text-white/55">
                                                {field.label}
                                            </dt>
                                            <dd className="whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-white/80">
                                                {field.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        ) : null}

                        {details.stack ? (
                            <details className="border border-white/10" dir="ltr">
                                <summary className="cursor-pointer px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white/55 hover:text-white">
                                    Stack trace
                                </summary>
                                <pre className="max-h-72 overflow-auto border-t border-white/10 p-4 font-mono text-[11px] leading-relaxed text-white/55">
                                    {details.stack}
                                </pre>
                            </details>
                        ) : null}

                        {info?.componentStack ? (
                            <details className="border border-white/10" dir="ltr">
                                <summary className="cursor-pointer px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white/55 hover:text-white">
                                    Component stack
                                </summary>
                                <pre className="max-h-56 overflow-auto border-t border-white/10 p-4 font-mono text-[11px] leading-relaxed text-white/55">
                                    {info.componentStack}
                                </pre>
                            </details>
                        ) : null}

                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                className="rounded-none"
                                onClick={retry}
                            >
                                נסה שוב
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                className="rounded-none"
                                onClick={copyReport}
                            >
                                {copyState === 'copied' ? (
                                    <Check />
                                ) : (
                                    <Copy />
                                )}
                                {copyState === 'copied'
                                    ? 'הועתק'
                                    : copyState === 'failed'
                                        ? 'ההעתקה נכשלה'
                                        : 'העתק דוח'}
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                className="rounded-none"
                            >
                                <Link to="/">חזרה לדף הבית</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="border-t border-white/10">
                <div className="mx-auto flex max-w-7xl items-center justify-between border-x border-white/10 px-6 py-6 text-xs text-white/55">
                    <span>© {new Date().getFullYear()} {SITE_NAME}</span>
                    <span className="font-mono text-[11px] text-white/55" dir="ltr">
                        error boundary
                    </span>
                </div>
            </footer>
        </div>
    );
}

/** Full-document shell for root-route failures (html/body replaced). */
export function RootErrorPage(props: ErrorComponentProps) {
    return (
        <html
            lang="he"
            dir="rtl"
            className="dark"
            style={{ backgroundColor: '#000000', colorScheme: 'dark' }}
            suppressHydrationWarning
        >
            <head>
                <HeadContent />
            </head>
            <body style={{ backgroundColor: '#000000' }}>
                <AppErrorPage {...props} />
                <Scripts />
            </body>
        </html>
    );
}

export function AppNotFoundPage() {
    return (
        <div
            dir="rtl"
            lang="he"
            className="dark flex min-h-screen flex-col bg-black text-[#ededed] antialiased"
        >
            <header className="border-b border-white/10">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between border-x border-white/10 px-6">
                    <Link to="/" className="inline-flex shrink-0 items-center">
                        <img
                            src="/images/logo.svg"
                            alt={SITE_NAME}
                            className="h-8 w-auto"
                            width={120}
                            height={32}
                        />
                    </Link>
                    <p className="font-mono text-[11px] text-white/55" dir="ltr">
                        404
                    </p>
                </div>
            </header>

            <main className="relative flex flex-1 flex-col px-6 py-12">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                        backgroundImage:
                            'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
                        backgroundSize: '28px 28px',
                        maskImage:
                            'radial-gradient(ellipse 60% 55% at 50% 30%, black, transparent 75%)',
                        WebkitMaskImage:
                            'radial-gradient(ellipse 60% 55% at 50% 30%, black, transparent 75%)',
                    }}
                />

                <div className="relative mx-auto w-full max-w-3xl border border-white/10 bg-black">
                    <Cross className="-start-[5px] -top-2.5" />
                    <Cross className="-end-[5px] -top-2.5" />
                    <Cross className="-bottom-2.5 -start-[5px]" />
                    <Cross className="-bottom-2.5 -end-[5px]" />

                    <div className="flex items-center justify-between gap-4 border-b border-white/10 px-6 py-3">
                        <p className="text-[10px] text-white/55">לא נמצא</p>
                        <span
                            className="border border-white/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white/55"
                            dir="ltr"
                        >
                            404
                        </span>
                    </div>

                    <div className="space-y-6 p-6 sm:p-8">
                        <div>
                            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                                העמוד לא נמצא
                            </h1>
                            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/50">
                                הכתובת שביקשת אינה קיימת, הוסרה, או שאינה זמינה
                                כעת.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button asChild className="rounded-none">
                                <Link to="/">חזרה לדף הבית</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="border-t border-white/10">
                <div className="mx-auto flex max-w-7xl items-center justify-between border-x border-white/10 px-6 py-6 text-xs text-white/55">
                    <span>© {new Date().getFullYear()} {SITE_NAME}</span>
                    <span className="font-mono text-[11px] text-white/55" dir="ltr">
                        not found
                    </span>
                </div>
            </footer>
        </div>
    );
}
